import { useEffect, useState } from "react";
import api from "../services/api";
import FormActions from "../components/Admin/FormActions/FormActions";
import { SuccessDialog } from "../components/Admin/Dialogs";
import { getImageUrl } from "../utils/media";
import "./AdminRodape.css";

const DEFAULT_SOCIALS = [
  { name: "Instagram", label: "Link do Instagram", url: "https://www.instagram.com/7geracoes/", iconUrl: "https://cdn.jsdelivr.net/npm/simple-icons@v13/icons/instagram.svg" },
  { name: "LinkedIn", label: "Link do LinkedIn", url: "https://www.linkedin.com/company/instituto-7-gerações/", iconUrl: "" },
  { name: "YouTube", label: "Link do YouTube", url: "https://www.youtube.com/@7geracoes962", iconUrl: "https://cdn.jsdelivr.net/npm/simple-icons@v13/icons/youtube.svg" },
];

const EMPTY_FOOTER = {
  logo: { url: "", public_id: "" },
  seal: { url: "", public_id: "" },
  logoLink: "/login",
  contact: { email: "comunicacao@7geracoes.com", phone: "(35) 3822-6251", cnpj: "28.932.203/0001-35" },
  socials: DEFAULT_SOCIALS,
  donationLink: "https://7geracoes.apoiar.co/",
  copyright: "2026 © 7 Gerações. Todos os direitos reservados.",
};

export default function AdminRodape() {
  const [footer, setFooter] = useState(EMPTY_FOOTER);
  const [draft, setDraft] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    api.get("/config/footer")
      .then((response) => {
        const savedSocials = response.data.socials || [];
        const socials = DEFAULT_SOCIALS.map((social) => ({
          ...social,
          url: savedSocials.find((saved) => saved.name === social.name)?.url || social.url,
        }));
        setFooter({
          ...EMPTY_FOOTER,
          ...response.data,
          socials,
          contact: { ...EMPTY_FOOTER.contact, ...response.data.contact },
        });
      })
      .catch(() => setError("Não foi possível carregar o rodapé."));
  }, []);

  const setContact = (field, value) =>
    setDraft((current) => ({
      ...current,
      contact: { ...current.contact, [field]: value },
    }));

  const setSocial = (index, field, value) =>
    setDraft((current) => ({
      ...current,
      socials: current.socials.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item,
      ),
    }));

  const uploadImage = async (event, field) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!["image/jpeg", "image/png"].includes(file.type) || file.size > 15 * 1024 * 1024) {
      setError("A imagem deve ser JPG ou PNG e ter no máximo 15 MB.");
      return;
    }
    const formData = new FormData();
    formData.append("image", file);
    try {
      const response = await api.post("/media/upload", formData);
      setDraft((current) => ({ ...current, [field]: response.data }));
      setError("");
    } catch {
      setError("Não foi possível enviar a imagem.");
    }
  };

  const save = async (event) => {
    event.preventDefault();
    setError("");

    const contactFields = [
      current.contact.email,
      current.contact.phone,
      current.contact.cnpj,
      current.logoLink,
      current.donationLink,
      current.copyright,
      ...current.socials.map((social) => social.url),
    ];

    if (contactFields.some((value) => !String(value || "").trim())) {
      setError("Preencha todos os campos obrigatórios antes de salvar.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...draft,
        logoLink: draft.logoLink.trim(),
        donationLink: draft.donationLink.trim(),
        copyright: draft.copyright.trim(),
        contact: Object.fromEntries(
          Object.entries(draft.contact).map(([key, value]) => [key, String(value || "").trim()]),
        ),
        socials: draft.socials.map((social) => ({
          ...social,
          url: String(social.url || "").trim(),
          iconUrl: String(social.iconUrl || "").trim(),
        })),
      };

      const response = await api.put("/config/footer", { footer: payload });
      setFooter(response.data);
      setDraft(null);
      setEditing(false);
      setSuccess("Alterações salvas com sucesso!");
    } catch (requestError) {
      setError(requestError.response?.data?.error || "Não foi possível salvar o rodapé.");
    } finally {
      setSaving(false);
    }
  };

  const startEditing = () => {
    setDraft(structuredClone(footer));
    setError("");
    setEditing(true);
  };

  const current = editing ? draft : footer;

  return (
    <main className="admin-rodape">
      <header className="admin-rodape__header"><h1>EDITAR SEÇÃO</h1><p>Rodapé</p></header>
      {error && <p className="admin-rodape__error" role="alert">{error}</p>}
      <form onSubmit={save} className="admin-rodape__form">
        <label>E-mail<input required value={current.contact.email} disabled={!editing} onChange={(event) => setContact("email", event.target.value)} /></label>
        <label>Telefone<input required value={current.contact.phone} disabled={!editing} onChange={(event) => setContact("phone", event.target.value)} /></label>
        <label>CNPJ<input required value={current.contact.cnpj} disabled={!editing} onChange={(event) => setContact("cnpj", event.target.value)} /></label>
        <label>Link da logo<input required value={current.logoLink} disabled={!editing} onChange={(event) => setDraft({ ...draft, logoLink: event.target.value })} /></label>
        <label>Link de doação<input required value={current.donationLink} disabled={!editing} onChange={(event) => setDraft({ ...draft, donationLink: event.target.value })} /></label>
        <label>Copyright<input required value={current.copyright} disabled={!editing} onChange={(event) => setDraft({ ...draft, copyright: event.target.value })} /></label>

        {current.socials.map((social, index) => (
          <label key={social.name}>
            {social.label || `Link do ${social.name}`}
            <input
              required
              disabled={!editing}
              value={social.url}
              onChange={(event) => setSocial(index, "url", event.target.value)}
            />
          </label>
        ))}

        <div className="admin-rodape__assets">
          {[{ field: "logo", label: "Logo 7 Gerações" }, { field: "seal", label: "Selo" }].map(({ field, label }) => (
            <label key={field} className="admin-rodape__asset">{label}
              <span className="admin-rodape__preview">{getImageUrl(current[field]) ? <img src={getImageUrl(current[field])} alt={label} /> : "Sem imagem"}</span>
              {editing && <input type="file" accept="image/jpeg,image/png" onChange={(event) => uploadImage(event, field)} />}
            </label>
          ))}
        </div>

        <FormActions editing={editing} onEdit={startEditing} onCancel={() => { setDraft(null); setEditing(false); setError(""); }} onSave={save} saving={saving} saveType="submit" />
      </form>
      <SuccessDialog open={Boolean(success)} message={success} onClose={() => setSuccess("")} />
    </main>
  );
}
